import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";

export const Header = () => {
  const { t, lang, setLang } = useLang();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const isPromo = pathname === "/" || pathname === "/promo";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  const onDark = isPromo && !scrolled;
  const solid = scrolled || !isPromo;

  const sec = (id) => (isPromo ? `#${id}` : `/promo#${id}`);
  const sections = [
    { id: "how", label: t.nav.how },
    { id: "menu", label: t.nav.menu },
    { id: "locations", label: t.nav.locations },
  ];
  const linkCls = `text-sm font-medium transition-colors duration-200 hover:text-tricolor-green ${onDark ? "text-white/80" : "text-ink/70"}`;

  return (
    <>
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        solid ? "bg-white/95 backdrop-blur-md border-b border-line shadow-[0_1px_20px_rgba(0,0,0,0.05)]" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1280px] px-5 md:px-8 h-16 md:h-[76px] flex items-center justify-between">
        <Link to="/promo" data-testid="header-logo-link">
          <Logo dark={onDark} className="h-10 md:h-12" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {sections.map((l) => (
            <a key={l.id} href={sec(l.id)} className={linkCls} data-testid={`nav-${l.id}`}>{l.label}</a>
          ))}
          <Link to="/franchising" className={linkCls} data-testid="nav-franchise">{t.nav.franchise}</Link>
          <Link to="/simulatore" className={linkCls} data-testid="nav-simulator">{t.nav.simulator}</Link>
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          <div className="flex items-center text-xs font-semibold tracking-wide" data-testid="lang-switcher" aria-label="Language">
            {["it", "en", "ru"].map((lc, i) => (
              <span key={lc} className="flex items-center">
                {i > 0 && <span className={onDark ? "text-white/30 mx-1" : "text-line mx-1"}>/</span>}
                <button
                  onClick={() => setLang(lc)}
                  className={`uppercase transition-colors ${lang === lc ? (onDark ? "text-white" : "text-ink") : (onDark ? "text-white/50 hover:text-white/80" : "text-muted hover:text-ink")}`}
                  data-testid={`lang-${lc}`}
                >
                  {lc}
                </button>
              </span>
            ))}
          </div>

          <Link to="/franchising" className="hidden md:inline-flex btn-primary !px-6 !py-2.5" data-testid="header-cta">{t.nav.cta}</Link>

          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-open" aria-label="Open menu">
            <Menu size={24} className={onDark ? "text-white" : "text-ink"} />
          </button>
        </div>
      </div>
    </header>

    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-white lg:hidden" data-testid="mobile-menu">
          <div className="px-5 h-16 flex items-center justify-between border-b border-line">
            <Logo className="h-9" />
            <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu"><X size={24} /></button>
          </div>
          <nav className="flex flex-col px-5 py-8 gap-5">
            {sections.map((l) => (
              <a key={l.id} href={sec(l.id)} onClick={() => setOpen(false)} className="font-display text-2xl font-bold tracking-tight" data-testid={`mobile-nav-${l.id}`}>{l.label}</a>
            ))}
            <Link to="/franchising" onClick={() => setOpen(false)} className="font-display text-2xl font-bold tracking-tight" data-testid="mobile-nav-franchise">{t.nav.franchise}</Link>
            <Link to="/simulatore" onClick={() => setOpen(false)} className="font-display text-2xl font-bold tracking-tight" data-testid="mobile-nav-simulator">{t.nav.simulator}</Link>
            <Link to="/franchising" onClick={() => setOpen(false)} className="mt-4 btn-primary" data-testid="mobile-cta">{t.nav.cta}</Link>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};
